import { useEffect, useState } from "react";
import type { CmmdWorkSession } from "@cmmd-forge/contracts";

import type { SidebarThreadSummary } from "~/types";
import { resolveTaskThread } from "~/lib/taskThreadLink";
import type { ExistingTaskConversation } from "../tasks/taskWorkSessionLaunch";
import { usePublishBreadcrumbLabel } from "../breadcrumb/globalBreadcrumbStore";
import { PROJECT_TAB_LABEL, PROJECT_TABS, type ProjectTab } from "./CmmdProjectTabs";

export function resolveExistingTaskThreadConversation(
  threadSummaries: readonly SidebarThreadSummary[],
  taskId: number,
): ExistingTaskConversation | null {
  const resolved = resolveTaskThread(threadSummaries, taskId);
  return resolved
    ? {
        kind: "thread",
        environmentId: resolved.thread.environmentId,
        threadId: resolved.thread.id,
      }
    : null;
}

export function resolveExistingTaskWorkSessionConversation(
  sessions: readonly CmmdWorkSession[],
  taskId: number,
): ExistingTaskConversation | null {
  let starting = false;
  for (const session of sessions) {
    if (session.taskId !== taskId) continue;
    const rootThread = session.threads.find((thread) => thread.kind === "root") ?? null;
    if (rootThread !== null) {
      return {
        kind: "thread",
        environmentId: session.forgeProject.environmentId,
        threadId: rootThread.forgeThreadId,
      };
    }
    if (session.status === "starting" || session.status === "active") starting = true;
  }
  return starting ? { kind: "starting" } : null;
}

/**
 * The project opens on its board.
 *
 * Overview was the landing tab, which meant every visit began on a summary of
 * work rather than on the work. Tasks is what the page is for, so it is both
 * the default and the tab that owns the bare URL; the others stay addressable
 * through `?tab=` even when the strip does not offer them.
 */
const DEFAULT_PROJECT_TAB: ProjectTab = "tasks";

/** Routable set, not the visible set — see `PROJECT_TABS` in `CmmdProjectTabs`. */
const ROUTABLE_PROJECT_TABS = new Set<ProjectTab>(PROJECT_TABS);

function readProjectTabFromLocation(): ProjectTab {
  if (typeof window === "undefined") return DEFAULT_PROJECT_TAB;
  const requested = new URLSearchParams(window.location.search).get("tab");
  return requested && ROUTABLE_PROJECT_TABS.has(requested as ProjectTab)
    ? (requested as ProjectTab)
    : DEFAULT_PROJECT_TAB;
}

/**
 * The active tab, and the strip's setter for it.
 *
 * Two things write `?tab=`: this hook's own `selectTab`, and the router when
 * something links into the page — the sidebar picker's settings gear does
 * exactly that. A router push does NOT emit `popstate` (TanStack notifies its
 * own subscribers instead), so the route hands its `tab` in as `requestedTab`
 * rather than this hook trying to hear a push it cannot. `null` means "the
 * URL is not asking for a tab", which leaves whatever the strip selected
 * alone instead of snapping back to the default.
 */
export function useProjectTab(
  requestedTab?: ProjectTab | null,
): readonly [ProjectTab, (tab: ProjectTab) => void] {
  const [activeTab, setActiveTab] = useState<ProjectTab>(readProjectTabFromLocation);
  usePublishBreadcrumbLabel("project-tasks", PROJECT_TAB_LABEL[activeTab]);

  useEffect(() => {
    const syncFromHistory = () => setActiveTab(readProjectTabFromLocation());
    window.addEventListener("popstate", syncFromHistory);
    return () => window.removeEventListener("popstate", syncFromHistory);
  }, []);

  useEffect(() => {
    if (requestedTab) setActiveTab(requestedTab);
  }, [requestedTab]);

  const selectTab = (tab: ProjectTab) => {
    const url = new URL(window.location.href);
    if (tab === DEFAULT_PROJECT_TAB) url.searchParams.delete("tab");
    else url.searchParams.set("tab", tab);
    window.history.pushState(null, "", url);
    setActiveTab(tab);
  };

  return [activeTab, selectTab] as const;
}
